import { useEffect, useRef } from 'react';

interface UseOutsideClickOptions {
	/** When false, the listener is not attached. */
	readonly isActive: boolean;
	/**
	 * DOM attribute used to mark elements that should count as "inside".
	 * A click on any element carrying this attribute (or any of its descendants)
	 * will NOT trigger `onOutsideClick`.
	 */
	readonly insideMarkerAttribute: string;
	/** Fired once per click that lands outside any marked element. */
	readonly onOutsideClick: () => void;
}

/**
 * Calls `onOutsideClick` when the user clicks anywhere on the page that is not
 * inside an element marked with `insideMarkerAttribute`.
 *
 * The marker-attribute pattern (instead of refs) lets a parent declare an
 * arbitrary number of "inside" zones — useful when a single component renders
 * a button + a floating dropdown that should both count as inside the same
 * boundary.
 */
export const useOutsideClick = ({
	isActive,
	insideMarkerAttribute,
	onOutsideClick,
}: UseOutsideClickOptions) => {
	const onOutsideClickRef = useRef(onOutsideClick);
	onOutsideClickRef.current = onOutsideClick;

	useEffect(() => {
		if (!isActive) return;

		const handleClick = (event: MouseEvent) => {
			const clickedInside = isElementInsideMarkedAncestor(event.target, insideMarkerAttribute);
			if (!clickedInside) {
				onOutsideClickRef.current();
			}
		};

		document.addEventListener('click', handleClick);
		return () => document.removeEventListener('click', handleClick);
	}, [isActive, insideMarkerAttribute]);
};

/**
 * Walks up from `target` until it finds an ancestor carrying
 * `markerAttribute`, or hits the document body. Returns true if the marker
 * was found along the way.
 */
const isElementInsideMarkedAncestor = (
	target: EventTarget | null,
	markerAttribute: string,
): boolean => {
	let currentNode = target as HTMLElement | null;

	while (currentNode && currentNode.nodeName !== 'BODY') {
		if (currentNode.hasAttribute(markerAttribute)) return true;
		currentNode = currentNode.parentElement;
	}

	return false;
};
